import type {
  SuppressHeaderKeyboardEventParams,
  SuppressKeyboardEventParams,
} from 'ag-grid-community';

const GRID_HEADER_CLASSNAME = 'ag-header-cell';
const GRID_CELL_CLASSNAME = 'ag-cell';

const getAllFocusableElementsOf = (el: HTMLElement) => {
  return Array.from<HTMLElement>(
    el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  ).filter((focusableEl) => {
    return focusableEl.tabIndex !== -1;
  });
};
const getEventPath = (event: Event): HTMLElement[] => {
  const path: HTMLElement[] = [];
  let currentTarget: HTMLElement | null = event.target as HTMLElement | null;
  while (currentTarget) {
    path.push(currentTarget);
    currentTarget = currentTarget.parentElement;
  }
  return path;
};
// `suppressHeaderKeyboardEvent`: This callback allows you to suppress specific keyboard events on the header cells.
// This allows for more granular control over keyboard navigation and interactions specifically in the grid's header.
// For instance, you might want to disable arrow key navigation within headers or prevent sorting when pressing certain keys.

export const suppressHeaderKeyboardEvent = ({
  event,
  disableHeaderCellFocus = false,
}: SuppressHeaderKeyboardEventParams & {
  disableHeaderCellFocus?: boolean;
}) => {
  const { key, shiftKey } = event;
  const path = getEventPath(event);
  const isTabForward = key === 'Tab' && !shiftKey;
  const isTabBackward = key === 'Tab' && shiftKey;
  let suppressEvent = false;
  // Handle cell children tabbing
  if (isTabForward || isTabBackward) {
    const eGridCell = path.find((el) => {
      if (el.classList === undefined) return false;
      return el.classList.contains(GRID_HEADER_CLASSNAME);
    });

    if (!eGridCell) {
      return suppressEvent;
    }
    const focusableChildrenElements = getAllFocusableElementsOf(eGridCell);
    const lastCellChildEl = focusableChildrenElements[focusableChildrenElements.length - 1];
    const firstCellChildEl = focusableChildrenElements[0];

    // Suppress keyboard event if tabbing forward within the cell and the current focused element is not the last child
    if (isTabForward) {
      if (disableHeaderCellFocus) {
        return true;
      }
      const isLastChildFocused = lastCellChildEl && document.activeElement === lastCellChildEl;
      if (!isLastChildFocused) {
        suppressEvent = true;
      }
    }
    // Suppress keyboard event if tabbing backwards within the cell, and the current focused element is not the first child
    else if (isTabBackward) {
      if (disableHeaderCellFocus) {
        return true;
      }
      const cellHasFocusedChildren =
        eGridCell.contains(document.activeElement) && eGridCell !== document.activeElement;
      // Manually set focus to the last child element if cell doesn't have focused children
      if (!cellHasFocusedChildren && lastCellChildEl) {
        lastCellChildEl.focus();
        // Cancel keyboard press, so that it doesn't focus on the last child and then pass through the keyboard press to
        // move to the 2nd last child element
        event.preventDefault();
      }
      const isFirstChildFocused = firstCellChildEl && document.activeElement === firstCellChildEl;
      if (!isFirstChildFocused) {
        suppressEvent = true;
      }
    }
  }
  return suppressEvent;
};

// `suppressKeyboardEvent`: This callback allows you to suppress specific keyboard events on the grid cells.
// It receives parameters like `event`, `editing`, `api`, and `column`, enabling you to filter keyboard actions
// based on the current context (e.g., the key pressed, whether the cell is in edit mode, etc.).
// Common use cases include preventing navigation or shortcut keys from triggering actions when certain conditions are met.

export function suppressKeyboardEvent({
  event,
  disableCellFocus = false,
}: SuppressKeyboardEventParams & {
  disableCellFocus?: boolean;
}) {
  const { key, shiftKey } = event;
  const path = getEventPath(event);
  const isTabForward = key === 'Tab' && !shiftKey;
  const isTabBackward = key === 'Tab' && shiftKey;

  let suppressEvent = false;

  // Handle cell children tabbing
  if (isTabForward || isTabBackward) {
    const eGridCell = path.find((el) => {
      if (!el.classList) return false;
      return el.classList.contains(GRID_CELL_CLASSNAME);
    });

    if (!eGridCell) return suppressEvent;

    const focusableChildrenElements = getAllFocusableElementsOf(eGridCell);
    if (focusableChildrenElements.length === 0) return false;
    const lastCellChildEl = focusableChildrenElements[focusableChildrenElements.length - 1];
    const firstCellChildEl = focusableChildrenElements[0];
    const currentIndex = focusableChildrenElements.indexOf(document.activeElement as HTMLElement);

    if (isTabForward) {
      const isLastChildFocused = document.activeElement === lastCellChildEl;
      if (isLastChildFocused && disableCellFocus) {
        eGridCell.classList.remove('ag-cell-focus');
        return false;
      }
      if (!isLastChildFocused) {
        suppressEvent = true;
        event.preventDefault();
        focusableChildrenElements[currentIndex + 1]?.focus();
      }
    } else if (isTabBackward) {
      const isFirstChildFocused = document.activeElement === firstCellChildEl;
      if (!isFirstChildFocused) {
        suppressEvent = true;
        event.preventDefault();
        if (currentIndex === -1 && lastCellChildEl) {
          // If the focus is on the cell itself, start from the last child element
          lastCellChildEl.focus();
        } else {
          focusableChildrenElements[currentIndex - 1]?.focus();
        }
      }
    }
    // Directly enter the first focusable child on Tab
    if ((currentIndex === -1 || currentIndex === 0) && isTabForward) {
      // suppressEvent = true;
      event.preventDefault();
      firstCellChildEl?.focus();
    }

    // Directly enter the last focusable child on Shift+Tab
    if (currentIndex === -1 && isTabBackward) {
      suppressEvent = true;
      event.preventDefault();
      lastCellChildEl?.focus();
    }
  }

  return suppressEvent;
}

export const removeCellFocusAndEnableLinkFocus = (
  event: KeyboardEvent,
  focusElementSelector: 'a' | 'button' | 'input' = 'a'
): boolean => {
  if (event.key === 'Tab') {
    const firstCell = document.querySelector('.ag-cell-focus');
    const firstFocusableElement = firstCell?.querySelector(focusElementSelector);
    if (firstCell && document.activeElement === firstCell) {
      event.preventDefault();
      firstCell.classList.remove('ag-cell-focus');
      firstFocusableElement?.focus();
    }
    return true;
  }
  return false;
};

export function suppressKeyboardEventWithTabGuard({ event, api }: SuppressKeyboardEventParams) {
  const { key, shiftKey } = event;
  const path = getEventPath(event);
  const isTabForward = key === 'Tab' && !shiftKey;
  const isTabBackward = key === 'Tab' && shiftKey;

  let suppressEvent = false;

  // Prevent focus from going to ag-tab-guard
  if (document.activeElement?.classList.contains('ag-tab-guard')) {
    event.preventDefault();
    return true;
  }

  // Handle cell navigation
  if (isTabForward || isTabBackward) {
    const eGridCell = path.find((el) => {
      if (!el.classList) return false;
      return el.classList.contains(GRID_CELL_CLASSNAME);
    });

    if (!eGridCell) return suppressEvent;

    const focusableChildrenElements = getAllFocusableElementsOf(eGridCell);
    if (focusableChildrenElements.length === 0) return false;

    const lastCellChildEl = focusableChildrenElements[focusableChildrenElements.length - 1];
    const firstCellChildEl = focusableChildrenElements[0];
    const currentIndex = focusableChildrenElements.indexOf(document.activeElement as HTMLElement);

    if (isTabForward) {
      const isLastChildFocused = lastCellChildEl && document.activeElement === lastCellChildEl;

      if (!isLastChildFocused) {
        suppressEvent = true;
        if (currentIndex !== -1 || document.activeElement === eGridCell) {
          event.preventDefault();
          focusableChildrenElements[currentIndex + 1]?.focus();
        }
      }
    } else {
      // Tabbing Backwards: Ensure we skip `suppressNavigable: true` cells
      const previousColumn = api
        .getDisplayedLeftColumns()
        .reverse()
        .find((col) => !col.getColDef().suppressNavigable);

      if (!previousColumn) return false; // No valid previous column

      const cellHasFocusedChildren =
        eGridCell.contains(document.activeElement) && eGridCell !== document.activeElement;

      if (!cellHasFocusedChildren) {
        lastCellChildEl?.focus();
        event.preventDefault();
      }

      const isFirstChildFocused = firstCellChildEl && document.activeElement === firstCellChildEl;
      if (!isFirstChildFocused) {
        suppressEvent = true;
        if (currentIndex !== -1 || document.activeElement === eGridCell) {
          event.preventDefault();
          focusableChildrenElements[currentIndex - 1]?.focus();
        }
      }
    }
  }

  return suppressEvent;
}

const EventKeys = ['TAB', 'ENTER'];
export const suppressKeyboardEventForTooltipSorting = (
  params: SuppressHeaderKeyboardEventParams
) => {
  const event = params.event;
  const key = event.key;
  const targetId = event?.target as HTMLElement;

  if (targetId.id.includes('tooltip')) {
    return true;
  }

  if (key.toUpperCase() === 'ENTER') {
    return false;
  }

  return EventKeys.includes(key.toUpperCase());
};

export const moveFocusToNextElement = (currentElement: HTMLElement, ids: string[] = []) => {
  const focusableElements = Array.from(
    document.querySelectorAll<HTMLElement>(
      'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex >= 0 && el.offsetParent !== null);
  const currentIndex = focusableElements.indexOf(currentElement);
  if (currentIndex >= 0 && currentIndex + 1 < focusableElements.length) {
    for (let i = currentIndex; i <= focusableElements.length; i++) {
      const nextFocusable = focusableElements[i];
      if (nextFocusable?.tagName === 'BUTTON' || ids.includes(nextFocusable?.id ?? '')) {
        nextFocusable?.focus();
        break;
      }
    }
  }
};
export const RemoveFocusToCell = (params: SuppressKeyboardEventParams<unknown, unknown>) => {
  const event = params.event;
  const key = event.key;
  const KEY_TAB = 'Tab';
  return KEY_TAB == key || key.toUpperCase() == KEY_TAB;
};
